// Utilities
import {parseJsonAttribute} from '../../utilities/utilities';

// Components
import Button from './button';
import Player from './player';

const settings = {
    dataAttributes: {
        parameters: 'data-audio-tooltip',
    },

    classNames: {
        pageHasAudioTooltip: 'has-audio-tooltip',
    },

    selectors: {
        tooltipDefinitions: 'section > .audio-tooltip',
        definitionPrompt: '.audio-tooltip__prompt',
        definitionTitle: '.audio-tooltip__title',
        definitionTranscript: '.audio-tooltip__transcript',
    },

    buttonInitialDelayScrollDistance: 300,

    animation: {
        buttonInitialDelay: 1250,
        buttonShowFromPlayerDelay: 0.25,
    },
};

const navModes = {
    start: 0,
    button: 1,
    player: 2,
};

export default class AudioTooltip {
    constructor(tooltipDefinition) {
        /** @type {number} */
        this.navMode = navModes.start;

        // Load our parameters
        this.parameters = parseJsonAttribute(
            tooltipDefinition,
            settings.dataAttributes.parameters,
        );

        if (!this.parameters.sources) {
            throw new Error('No sources provided for audio tooltip');
        }

        // Remove the element from the page
        tooltipDefinition.remove();

        // Scrape the title and transcript HTML from
        // the definition element
        const prompt = tooltipDefinition
            .querySelector(settings.selectors.definitionPrompt);

        const title = tooltipDefinition
            .querySelector(settings.selectors.definitionTitle);

        const transcript = tooltipDefinition
            .querySelector(settings.selectors.definitionTranscript);

        // Create our button
        /** @type {Button} */
        this.button = new Button(
            prompt,
            (event) => {
                this.navigate(
                    navModes.player,
                    false,
                    event.shiftKey,
                );
            },
        );

        // Create our player
        /** @type {Player} */
        this.player = new Player(
            title,
            transcript,
            this.parameters,
            (event) => {
                this.navigate(
                    navModes.button,
                    false,
                    event.shiftKey,
                );
            },
        );

        // Show button after a delay
        // ...and do this on the next tick so that the scroll lock
        // stuff has time to initialize
        setTimeout(
            () => {
                if (
                    window.pageYOffset >=
                    settings.buttonInitialDelayScrollDistance
                ) {
                    this.navigate(navModes.button);
                } else {
                    setTimeout(
                        () => {
                            this.navigate(navModes.button);
                        },
                        settings.animation.buttonInitialDelay,
                    );
                }
            },
            250,
        );
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .querySelectorAll(settings.selectors.tooltipDefinitions);

        // EDIT: For now, just initialize the first tooltip found on
        // the page - eventually the plan is to have these appear
        // beside their associated section on the page
        // for (let i=0; i<elements.length; i++) {
        //     new AudioTooltip(elements[i]);
        // }

        if (elements.length > 0) {
            // Add class to the <body> element to indicate that an audio tooltip is
            // present; we do this to add additional padding to the footer so that
            // it's not obscured by the button
            document.body.classList
                .add(settings.classNames.pageHasAudioTooltip);

            // Initialize the first tooltip found
            new AudioTooltip(elements[0]);
        }
    }

    navigate(
        navMode,
        immediate = false,
        slowMo = false,
    ) {
        if (navMode == this.navMode) {
            return;
        }

        switch (navMode) {
            case navModes.button:
                switch (this.navMode) {
                    case navModes.start:
                        this.button.show(0, immediate, slowMo);
                        break;

                    case navModes.player:
                        this.button.show(
                            settings.animation.buttonShowFromPlayerDelay,
                            immediate,
                            slowMo,
                        );
                        this.player.hide(immediate, slowMo);
                        break;

                    default:
                        // Stop here
                        return;
                }
                break;

            case navModes.player:
                switch (this.navMode) {
                    case navModes.button:
                        this.button.hide(immediate, slowMo);
                        this.player.show(immediate, slowMo);
                        break;

                    default:
                        // Stop here
                        return;
                }
                break;

            default:
                // Stop here
                return;
        }

        // Update our nav mode if we chose to navigate
        this.navMode = navMode;
    }
}
